// frontend/src/pages/AdminSanqiErrorManagement.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Badge, Modal } from 'react-bootstrap';
import api from '../api';

const AdminSanqiErrorManagement = () => {
  const [errorSessions, setErrorSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [showAlert, setShowAlert] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [operationType, setOperationType] = useState('');

  // 获取异常会话列表
  const fetchErrorSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/sanqi/error-sessions');
      if (response.data.success) {
        setErrorSessions(response.data.errorSessions);
      }
    } catch (error) {
      console.error('获取异常会话失败:', error);
      showAlertMessage('获取异常会话列表失败', 'danger');
    }
    setLoading(false);
  };

  // 显示提示消息
  const showAlertMessage = (message, variant = 'success') => {
    setAlertMessage(message);
    setAlertVariant(variant);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  // 确认操作
  const confirmOperation = (session, type) => {
    setSelectedSession(session);
    setOperationType(type);
    setShowConfirmModal(true);
  };

  // 执行操作
  const executeOperation = async () => {
    if (!selectedSession) return;

    try {
      let response;
      if (operationType === 'retry') {
        response = await api.post('/api/admin/sanqi/retry', {
          sessionId: selectedSession.sessionId
        });
      } else if (operationType === 'complete') {
        response = await api.post('/api/admin/sanqi/force-complete', {
          sessionId: selectedSession.sessionId,
          reason: '管理员强制完成'
        });
      }

      if (response.data.success) {
        showAlertMessage(response.data.message, 'success');
        await fetchErrorSessions(); // 刷新列表
      } else {
        showAlertMessage(response.data.message || '操作失败', 'danger');
      }
    } catch (error) {
      console.error('操作失败:', error);
      showAlertMessage(error.response?.data?.message || '操作失败', 'danger');
    }
    setShowConfirmModal(false);
  };

  // 格式化时间
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  // 计算游戏持续时间
  const getGameDuration = (startTime) => {
    if (!startTime) return '-';
    const duration = Date.now() - startTime;
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}分${seconds}秒`;
  };

  useEffect(() => {
    fetchErrorSessions();
    // 设置定时刷新
    const interval = setInterval(fetchErrorSessions, 30000); // 30秒刷新一次
    return () => clearInterval(interval);
  }, []);

  return (
    <Container fluid className="p-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                三七机通信异常管理
              </h5>
              <div>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={fetchErrorSessions}
                  disabled={loading}
                >
                  <i className="fas fa-sync-alt me-1"></i>
                  {loading ? '刷新中...' : '刷新'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {/* 提示消息 */}
              {showAlert && (
                <Alert variant={alertVariant} dismissible onClose={() => setShowAlert(false)}>
                  {alertMessage}
                </Alert>
              )}

              {/* 统计信息 */}
              <div className="mb-3">
                <Badge bg="warning" className="me-2">
                  异常会话数量: {errorSessions.length}
                </Badge>
                <small className="text-muted">
                  页面每30秒自动刷新 | 最后刷新: {new Date().toLocaleTimeString()}
                </small>
              </div>

              {/* 异常会话列表 */}
              {errorSessions.length === 0 ? (
                <Alert variant="info" className="text-center">
                  <i className="fas fa-check-circle me-2"></i>
                  当前没有通信异常的三七机会话
                </Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>会话ID</th>
                      <th>用户</th>
                      <th>设备</th>
                      <th>游戏进度</th>
                      <th>已退还积分</th>
                      <th>开始时间</th>
                      <th>持续时间</th>
                      <th>状态</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorSessions.map((session) => (
                      <tr key={session.sessionId}>
                        <td>
                          <code>{session.sessionId}</code>
                        </td>
                        <td>
                          <div>
                            <div className="fw-bold">{session.username}</div>
                            <small className="text-muted">ID: {session.userId}</small>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="fw-bold">{session.deviceName}</div>
                            <small className="text-muted">TCP: {session.deviceTcpId}</small>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="fw-bold">
                              {session.completedTurns}/{session.selectedTurns}
                            </div>
                            <small className="text-muted">
                              剩余: {session.remainingTurns}转
                            </small>
                          </div>
                        </td>
                        <td>
                          <Badge bg="success">
                            {session.refundedPoints} 积分
                          </Badge>
                        </td>
                        <td>
                          <small>{formatTime(session.startTime)}</small>
                        </td>
                        <td>
                          <small>{getGameDuration(session.startTime)}</small>
                        </td>
                        <td>
                          <Badge bg="warning">通信异常</Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => confirmOperation(session, 'retry')}
                              title="重试游戏"
                            >
                              <i className="fas fa-redo"></i>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => confirmOperation(session, 'complete')}
                              title="强制完成"
                            >
                              <i className="fas fa-stop"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 确认操作模态框 */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            确认操作
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSession && (
            <div>
              <p>
                <strong>会话:</strong> {selectedSession.sessionId}
              </p>
              <p>
                <strong>用户:</strong> {selectedSession.username}
              </p>
              <p>
                <strong>设备:</strong> {selectedSession.deviceName} ({selectedSession.deviceTcpId})
              </p>
              <p>
                <strong>游戏进度:</strong> {selectedSession.completedTurns}/{selectedSession.selectedTurns} 转
              </p>
              
              {operationType === 'retry' && (
                <Alert variant="info">
                  <strong>重试游戏:</strong> 将重新发送 ###sq 指令，继续当前轮次的游戏。
                </Alert>
              )}
              
              {operationType === 'complete' && (
                <Alert variant="warning">
                  <strong>强制完成:</strong> 将强制结束游戏并释放设备，剩余转数不会继续执行。
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            取消
          </Button>
          <Button 
            variant={operationType === 'retry' ? 'success' : 'danger'} 
            onClick={executeOperation}
          >
            {operationType === 'retry' ? '确认重试' : '确认完成'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminSanqiErrorManagement;
